import { ReactElement } from "react";
import { TranslationKeys } from "translations";
import { DateHelper, LocalizationHelperInstance, PeriodisationRowsHelper } from "utils/helpers";
import { UtilFormTypes } from "models/formTypes";
import { PeriodisationRowsFormValue } from "models/formTypes/incomingInvoice";
import { ProductPeriodisation } from "models/formTypes/invoice";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { KeyValue } from "models/utilTypes/keyValue";

const AccrualHelper = {
  getPeriodisationExists: (productPeriodisation: ProductPeriodisation[]) => {
    return productPeriodisation.some(
      (product) =>
        product.periodisation !== undefined &&
        (product.periodisation.start !== undefined || product.periodisation.end !== undefined)
    );
  },

  getIsAdvancedConfiguration: (productPeriodisation?: ProductPeriodisation[]) => {
    if (productPeriodisation === undefined || !AccrualHelper.getPeriodisationExists(productPeriodisation)) return false;

    const isAdvancedConfiguration = productPeriodisation?.some(
      (row, index, arr) =>
        row.periodisation?.start?.getTime() !== arr[0].periodisation?.start?.getTime() ||
        row.periodisation?.end?.getTime() !== arr[0].periodisation?.end?.getTime()
    );

    return isAdvancedConfiguration;
  },

  getIsSeveralMonths: (productPeriodisation?: ProductPeriodisation[]) => {
    if (productPeriodisation === undefined || !AccrualHelper.getPeriodisationExists(productPeriodisation)) {
      return false;
    }

    const startFirstProduct = productPeriodisation[0].periodisation?.start
      ? new Date(productPeriodisation[0].periodisation.start)
      : undefined;
    const endFirstProduct = productPeriodisation[0].periodisation?.end
      ? new Date(productPeriodisation[0].periodisation.end)
      : undefined;

    if (
      startFirstProduct !== undefined &&
      endFirstProduct !== undefined &&
      DateHelper.getMonth(endFirstProduct) !== DateHelper.getMonth(startFirstProduct)
    )
      return true;

    const isSeveralMonths = productPeriodisation?.some(
      (row, index, arr) =>
        row.periodisation?.start?.getMonth() !== arr[0].periodisation?.start?.getMonth() ||
        row.periodisation?.end?.getMonth() !== arr[0].periodisation?.end?.getMonth()
    );

    return isSeveralMonths;
  },

  getAccuralSummary: (periodisation?: UtilFormTypes.DateRange) => {
    if (periodisation === undefined) return "";

    const start = periodisation.start ? new Date(periodisation.start) : undefined;
    const end = periodisation.end ? new Date(periodisation.end) : undefined;
    let summary = "";

    if (
      start !== undefined &&
      end !== undefined &&
      (DateHelper.getYear(end) !== DateHelper.getYear(start) || DateHelper.getMonth(end) !== DateHelper.getMonth(start))
    ) {
      summary = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.BookedInSeveralMonths);
    } else if (start !== undefined || end !== undefined) {
      const date = start ?? end;
      const month = date ? DateHelper.getMonth(date) : "";
      const monthLong = LocalizationHelperInstance.getFormattedMessage(`Common.Month_${month}`);
      summary = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.BookedInMonth, "", {
        monthbooked: monthLong,
      });
    } else {
      summary = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional);
    }

    return summary;
  },

  getRowsAccrualSummary: (value: PeriodisationRowsFormValue | undefined, invoiceMonth: Date | undefined) => {
    // A split lying wholly in the invoice month is saved as no periodisation, so it must not read as several months.
    if (
      !value ||
      !PeriodisationRowsHelper.hasPeriodisation(value) ||
      PeriodisationRowsHelper.isSplitAllInInvoiceMonth(value, invoiceMonth)
    )
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional);
    if (value.isSplit && value.rows.length > 1)
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.BookedInSeveralMonths);
    return AccrualHelper.getAccuralSummary(value.rows[0]?.range);
  },

  getRowAccuralSummary: (productPeriodisation?: ProductPeriodisation[]) => {
    if (productPeriodisation === undefined || !AccrualHelper.getPeriodisationExists(productPeriodisation))
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional);

    if (AccrualHelper.getIsAdvancedConfiguration(productPeriodisation)) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.BookedInSeveralMonths);
    } else return AccrualHelper.getAccuralSummary(productPeriodisation[0].periodisation);
  },

  getSimplifiedPeriodisationKeyValueArray: (periodisation: ProductPeriodisation[]) => {
    const first = periodisation[0];
    const formatRange =
      first.mode === "date"
        ? DateHelper.formatDateRange(first.periodisation!.start ?? undefined, first.periodisation!.end ?? undefined)
        : DateHelper.formatMonthRange(first.periodisation!.start ?? undefined, first.periodisation!.end ?? undefined);
    return [
      new KeyValue(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AllArticles), formatRange),
    ];
  },

  // Supplier invoice rows, shown the way getAdvancedPeriodisationKeyValueArray shows customer invoice rows.
  // Rows hold positive amounts; a credit invoice shows them negative, as the advanced dialog does.
  getPeriodisationRowsKeyValueArray: (
    value: PeriodisationRowsFormValue,
    netAmount: number,
    currency?: string,
    isCredit?: boolean
  ) => {
    const formatAmount = (amount: number) =>
      LocalizationHelperInstance.getFormattedCurrency(isCredit && amount !== 0 ? -amount : amount, currency, 2, 2);
    const formatRange = (range?: UtilFormTypes.DateRange) =>
      value.mode === "date"
        ? DateHelper.formatDateRange(range?.start ?? undefined, range?.end ?? undefined)
        : DateHelper.formatMonthRange(range?.start ?? undefined, range?.end ?? undefined);

    if (!value.isSplit) return [new KeyValue(formatAmount(Math.abs(netAmount)), formatRange(value.rows[0]?.range))];

    return value.rows.reduce(
      (acc: Array<KeyValue<string, string | ReactElement> | typeof ACTION_SEPARATOR>, row, index) => {
        if (index !== 0) acc.push(ACTION_SEPARATOR);
        acc.push(new KeyValue(formatAmount(row.amount ?? 0), formatRange(row.range)));
        return acc;
      },
      []
    );
  },

  getAdvancedPeriodisationKeyValueArray: (periodisation: ProductPeriodisation[]) => {
    return periodisation.reduce(
      (acc: Array<KeyValue<string, string | ReactElement> | typeof ACTION_SEPARATOR>, curr, index) => {
        if (curr.periodisation) {
          if (index !== 0 && acc.length > 0 && acc[acc.length - 1] !== ACTION_SEPARATOR) {
            acc.push(ACTION_SEPARATOR);
          }
          const formatRange =
            curr.mode === "date"
              ? DateHelper.formatDateRange(curr.periodisation.start ?? undefined, curr.periodisation.end ?? undefined)
              : DateHelper.formatMonthRange(curr.periodisation.start ?? undefined, curr.periodisation.end ?? undefined);
          const label =
            curr.text && curr.text.trim().length > 0
              ? curr.text
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Product);
          acc.push(new KeyValue(label, formatRange));
        }
        return acc;
      },
      []
    );
  },
};

export default AccrualHelper;
